import { useMemo, useState, type ReactElement, type ReactNode } from 'react';

import { createPollCycleStore } from './pollCycle.js';
import { CUE_STORAGE_KEY, PollCycleContext, type PollCycleValue } from './usePollCycle.js';

const isCueStoredAsOn = (): boolean => localStorage.getItem(CUE_STORAGE_KEY) !== 'off';

/**
 * Holds the app's one poll cycle, and whether the cue built on it is shown at
 * all. Both live here rather than in each page: the cycle because every cue on
 * screen is tracking the same fetch, and the preference because turning the cue
 * off has to turn off the timer behind it too.
 */
export const PollCycleProvider = ({ children }: { readonly children: ReactNode }): ReactElement => {
  const [store] = useState(createPollCycleStore);
  const [isCueEnabled, setCueEnabled] = useState(isCueStoredAsOn);

  const value = useMemo<PollCycleValue>(
    () => ({
      store,
      isCueEnabled,
      setCueEnabled: (isEnabled): void => {
        localStorage.setItem(CUE_STORAGE_KEY, isEnabled ? 'on' : 'off');
        setCueEnabled(isEnabled);
      },
    }),
    [store, isCueEnabled],
  );

  return <PollCycleContext value={value}>{children}</PollCycleContext>;
};
